import { clamp } from '@infinex-private/ts-utils/number';

export type Rgb = [number, number, number];
export type Rgba = [number, number, number, number];

/**
 * Get the contrast colour for a given hex colour.
 * @see https://stackoverflow.com/a/9733420
 */
export function getContrastColor(hex: string) {
  const rgb = hexToRgb(hex);

  const bg = luminance(rgb);
  const fg = luminance([0, 0, 0]);
  const ratio = (bg + 0.05) / (fg + 0.05);

  return ratio > 6 ? '#000000' : '#FFFFFF';
}

/**
 * Calculate the luminance of a colour.
 * @see https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 */
export function luminance(rgb: Rgb) {
  const a = rgb.map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    // Array.map() widens the tuple type to number[]; the type assertion is needed because TypeScript can't preserve the exact tuple length through the map operation.
  }) as Rgb;

  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
}

/**
 * Cleans a hex colour string by removing the leading `#` if present, and
 * expanding shorthand hex codes.
 */
export function cleanHex(hex: string) {
  const cleanedHex = hex.startsWith('#') ? hex.slice(1) : hex;

  // handle shorthand
  let fullHex = cleanedHex;
  if (cleanedHex.length === 3 || cleanedHex.length === 4) {
    fullHex = cleanedHex
      .split('')
      .map((char) => char + char)
      .join('');
  }

  // validate hex length (6, or 8 with alpha channel)
  if (fullHex.length !== 6 && fullHex.length !== 8) {
    throw new Error(`Invalid hex colour: ${hex}`);
  }

  return fullHex;
}

/**
 * Adds or replaces the alpha channel of a hex color.
 *
 * @param hex - Hex color string (with or without alpha channel)
 * @param opacity - Alpha value between 0 and 1
 * @returns Hex color with the specified alpha channel, including `#` prefix
 */
export function hexAlpha(hex: string, opacity: number) {
  const cleanedHex = cleanHex(hex);
  const baseHex = cleanedHex.length === 8 ? cleanedHex.slice(0, 6) : cleanedHex;

  return `#${baseHex}${hexOpacity(opacity)}`;
}

/** Converts a hex color to an RGB tuple. */
export function hexToRgb(hex: string): Rgb {
  const cleanedHex = cleanHex(hex);

  return [
    parseInt(cleanedHex.slice(0, 2), 16),
    parseInt(cleanedHex.slice(2, 4), 16),
    parseInt(cleanedHex.slice(4, 6), 16),
  ];
}

/**
 * Converts a hex color to an RGBA tuple.
 * @returns The RGBA tuple, with alpha normalized to 0-1
 */
export function hexToRgba(hex: string): Rgba {
  const cleanedHex = cleanHex(hex);
  const rgb = hexToRgb(hex);

  // extract alpha if present, otherwise default to 1
  const alpha =
    cleanedHex.length === 8 ? parseInt(cleanedHex.slice(6, 8), 16) / 255 : 1;

  return [...rgb, alpha];
}

/** Converts an RGB or RGBA tuple to a hex color. */
export function rgbToHex(value: Rgb | Rgba): string {
  const [r, g, b, a] = value;
  const rbg = [r, g, b].map(toHexString);
  // configurable alpha channel, omit when opaque? e.g. `(a !== undefined && a !== 1)`
  if (a !== undefined) return `#${rbg.join('')}${hexOpacity(a)}`;
  return `#${rbg.join('')}`;
}

/** Converts an RGB or RGBA tuple to a CSS string. */
export function rgbToCssString(
  value: Rgb | Rgba,
  options: { legacySyntax?: boolean } = {}
): string {
  const [r, g, b, a] = value;
  const { legacySyntax } = options;
  const hasAlpha = a !== undefined && a !== 1;

  if (legacySyntax) {
    if (hasAlpha) return `rgba(${r}, ${g}, ${b}, ${a})`;
    return `rgb(${r}, ${g}, ${b})`;
  }

  if (hasAlpha) return `rgb(${r} ${g} ${b} / ${a})`;
  return `rgb(${r} ${g} ${b})`;
}

// Internal --------------------------------------------------------------------

function hexOpacity(opacity: number) {
  return toHexString(Math.round(opacity * 255));
}

function toHexString(value: number) {
  return clamp(value, 0, 255).toString(16).padStart(2, '0');
}
